import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import CookieBannerContext from 'contexts/CookieBannerContext'

import useCookies from 'hooks/useCookies'
import useFetch from 'hooks/useFetch'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

const CookieBannerProvider = ({ children, bannerId }) => {
  const [activeStep, setActiveStep] = useState(0)
  const [isAgeGateCompiling, setIsAgeGateCompiling] = useState(false)
  const [isCompiling, setIsCompiling] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [appearanceValues, setAppearanceValues] = useState({
    primaryColor: null,
    secondaryColor: null,
    fontFamily: null,
  })
  const [selectedLanguage, setSelectedLanguage] = useState(
    constants.cookies.language.DEFAULT_LANGUAGE,
  )
  // Estrutura: [ { languageCode: 'pt-BR', fields: [ { field: 'banner.title', value: '...' } ] } ]
  const [bannerTextsOverrides, setBannerTextsOverrides] = useState([])
  // Estrutura: [ { languageCode: 'pt-BR', fields: [ { field: 'ageControl.title', value: '...' } ] } ]
  const [ageGateTextsOverrides, setAgeGateTextsOverrides] = useState([])

  // Ref para armazenar a função de submit da etapa atual
  const currentStepSubmitRef = useRef(null)

  const { response, isLoading, refresh } = useCookies(
    service.cookies.banner.get,
    { bannerId: bannerId },
  )

  const { response: ageGateResponse, refresh: refreshAgeGate } = useFetch(
    service.cookies.ageGate.get,
    { bannerId },
    [bannerId],
    !!bannerId,
  )

  const banner = response?.data?.banner
  const ageGate = ageGateResponse?.data?.ageGate
  const { i18n } = useTranslation()

  const refreshBanner = useCallback(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (ageGate) {
      setIsAgeGateCompiling(helpers.cookies.banner.isAgeGateCompiling(ageGate))
    }
  }, [ageGate])

  useEffect(() => {
    if (banner) {
      setIsCompiling(helpers.cookies.banner.isBannerCompiling(banner))
      setIsScanning(helpers.cookies.banner.isBannerScanning(banner))
      setBannerTextsOverrides(banner?.bannerTextsOverrides)
      setAppearanceValues((prev) => ({
        primaryColor: prev.primaryColor ?? banner.primaryColor ?? null,
        secondaryColor: prev.secondaryColor ?? banner.secondaryColor ?? null,
        fontFamily: prev.fontFamily ?? banner.fontFamily ?? null,
      }))
    }
  }, [banner])

  useEffect(() => {
    if (selectedLanguage && i18n.language !== selectedLanguage) {
      i18n.changeLanguage(selectedLanguage)
    }
  }, [selectedLanguage, i18n])

  const nextStep = () => {
    setActiveStep((prev) => prev + 1)
  }

  const backStep = () => {
    setActiveStep((prev) => Math.max(0, prev - 1))
  }

  const goToStep = (step) => {
    if (step >= 0) setActiveStep(step)
  }

  const updateBannerTextOverride = (field, value) => {
    const languageCode = selectedLanguage
    if (!languageCode || !field) return
    setBannerTextsOverrides((previousOverrides) => {
      const updatedOverrides = [...previousOverrides]
      let languageGroup = updatedOverrides.find(
        (group) => group.languageCode === languageCode,
      )
      if (!languageGroup) {
        languageGroup = { languageCode, fields: [] }
        updatedOverrides.push(languageGroup)
      }
      const fieldPosition = languageGroup.fields.findIndex(
        (fieldItem) => fieldItem.field === field,
      )
      if (fieldPosition === -1) {
        languageGroup.fields.push({ field, value: value ?? '' })
      } else {
        languageGroup.fields[fieldPosition] = { field, value: value ?? '' }
      }
      return updatedOverrides
    })
  }

  const getBannerTextOverride = (field) => {
    const languageCode = selectedLanguage
    if (!languageCode || !field) return ''
    const languageGroup = bannerTextsOverrides.find(
      (group) => group.languageCode === languageCode,
    )
    if (!languageGroup) return ''
    const fieldEntry = languageGroup.fields.find(
      (fieldItem) => fieldItem.field === field,
    )
    return fieldEntry ? fieldEntry.value : ''
  }

  const updateAgeGateTextOverride = (field, value) => {
    const languageCode = selectedLanguage
    if (!languageCode || !field) return
    setAgeGateTextsOverrides((previousOverrides) => {
      const updatedOverrides = [...previousOverrides]
      let languageGroup = updatedOverrides.find(
        (group) => group.languageCode === languageCode,
      )
      if (!languageGroup) {
        languageGroup = { languageCode, fields: [] }
        updatedOverrides.push(languageGroup)
      }
      const fieldPosition = languageGroup.fields.findIndex(
        (fieldItem) => fieldItem.field === field,
      )
      if (fieldPosition === -1) {
        languageGroup.fields.push({ field, value: value ?? '' })
      } else {
        languageGroup.fields[fieldPosition] = { field, value: value ?? '' }
      }
      return updatedOverrides
    })
  }

  const getAgeGateTextOverride = (field) => {
    const languageCode = selectedLanguage
    if (!languageCode || !field) return ''
    const languageGroup = ageGateTextsOverrides.find(
      (group) => group.languageCode === languageCode,
    )
    if (!languageGroup) return ''
    const fieldEntry = languageGroup.fields.find(
      (fieldItem) => fieldItem.field === field,
    )
    return fieldEntry ? fieldEntry.value : ''
  }

  const setStepSubmit = (submitFunction) => {
    currentStepSubmitRef.current = submitFunction
  }

  const executeStepSubmit = async () => {
    if (currentStepSubmitRef.current) {
      await currentStepSubmitRef.current()
    }
  }

  return (
    <CookieBannerContext.Provider
      value={{
        // Estados
        activeStep,
        ageGate,
        appearanceValues,
        banner,
        loading: isLoading || isSubmitting,
        isAgeGateCompiling,
        isCompiling,
        isScanning,
        isSubmitting,
        ageGateTextsOverrides,
        bannerTextsOverrides,
        selectedLanguage,

        // Setters
        setActiveStep,
        setAgeGateTextsOverrides,
        setAppearanceValues,
        setIsAgeGateCompiling,
        setIsCompiling,
        setIsScanning,
        setIsSubmitting,
        setSelectedLanguage,

        // Funções de navegação
        nextStep,
        backStep,
        goToStep,

        // Funções de dados
        refreshAgeGate,
        refreshBanner,
        updateBannerTextOverride,
        getBannerTextOverride,
        updateAgeGateTextOverride,
        getAgeGateTextOverride,

        // Funções de submit
        setStepSubmit,
        executeStepSubmit,

        // Dados derivados
        cookies: helpers.cookies.banner.extractCookiesFromBanner(banner),
      }}
    >
      {children}
    </CookieBannerContext.Provider>
  )
}

export default CookieBannerProvider
